import { isNil } from 'lodash';
import { useCallback } from 'react';
import { ActionSheetOption } from '@shared/components/ActionSheet';
import { useToast } from '@shared/components/toast';
import { API } from '@shared/types/api';
import { useBoolean } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { useRemoveCard } from '../hooks';

interface RemoveCardMenuState {
  isOpen: boolean;
  openRemoveMenu: VoidFunction;
  closeRemoveMenu: VoidFunction;
  removeMenuTitle: string;
  removeMenuOptions: ActionSheetOption[];
}

export function useRemoveCardWithConfirmation(
  card: API.CardFragment,
  onRemoved?: () => void,
): RemoveCardMenuState {
  const removeCard = useRemoveCard();
  const toast = useToast();
  const [isOpen, openRemoveMenu, closeRemoveMenu] = useBoolean();

  const remove = useCallback(
    async (scope: API.EntityRecurrenceScope) => {
      await removeCard(card.id, scope);
      onRemoved?.();
      toast?.show({
        icon: 'check-filled',
        message: i18n('card.details.deleteCompleted', { card: card.fields?.name }),
      });
    },
    [removeCard, card, onRemoved, toast],
  );

  const removeMenuTitle = !isNil(card.recurrence)
    ? i18n('card.details.deleteScopeActionSheet.title')
    : i18n('card.details.deleteConfirmation.title');

  const removeMenuOptions: ActionSheetOption[] = !isNil(card.recurrence)
    ? [
        {
          title: i18n('card.details.deleteScopeActionSheet.currentOnly'),
          onPress: () => void remove('ENTITY'),
        },
        {
          title: i18n('card.details.deleteScopeActionSheet.currentAndUpcoming'),
          onPress: () => void remove('ENTITY_UPCOMING'),
        },
      ]
    : [
        {
          title: i18n('card.details.deleteConfirmation.confirm'),
          destructive: true,
          onPress: () => void remove('ENTITY'),
        },
        {
          title: i18n('common.cancel'),
          onPress: closeRemoveMenu,
        },
      ];

  return {
    isOpen,
    openRemoveMenu,
    closeRemoveMenu,
    removeMenuTitle,
    removeMenuOptions,
  };
}
